import { Ban, Target } from 'lucide-react';
import { emptyPlaygroundExpectation, type PlaygroundExpectation } from '@repo/shared-types';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';

/** A project as the picker lists it — id, name and the path that disambiguates two of a name. */
export interface ExpectationProject {
  id: string;
  name: string;
  path: string;
  depth: number;
}

/**
 * What a case asserts about the agent, which is what turns a rehearsal into a test.
 *
 * Deliberately projects and thresholds rather than an expected output to diff against. Placement has
 * no single right answer — a partner call covering two engagements genuinely belongs in both, and a
 * split that is merely *different* is not a regression. What a person can honestly assert is which
 * projects must be reached, which must not, and how much of the content had to end up somewhere.
 */
export function ExpectationEditor({
  projects,
  value,
  onChange,
  disabled,
  mode = 'arrival',
}: {
  projects: ExpectationProject[];
  value: PlaygroundExpectation | null;
  onChange: (next: PlaygroundExpectation | null) => void;
  disabled?: boolean;
  /** `question` grades a retrieval run: pages it should cite, projects its citations come from. */
  mode?: 'arrival' | 'question';
}) {
  const expectation = value ?? emptyPlaygroundExpectation();

  const set = (patch: Partial<PlaygroundExpectation>) => onChange({ ...expectation, ...patch });

  const toggle = (key: 'expectProjectIds' | 'forbidProjectIds', id: string) => {
    const current = expectation[key];
    const next = current.includes(id) ? current.filter((one) => one !== id) : [...current, id];
    // A project cannot be both expected and forbidden; picking one side clears the other.
    const other = key === 'expectProjectIds' ? 'forbidProjectIds' : 'expectProjectIds';
    set({ [key]: next, [other]: expectation[other].filter((one) => one !== id) } as Partial<PlaygroundExpectation>);
  };

  const question = mode === 'question';

  return (
    <div className="space-y-3">
      {question && (
        <label className="block">
          <span className="mb-1 block text-[10px] font-medium text-ink-secondary">Pages it should cite</span>
          <textarea
            value={(expectation.expectPaths ?? []).join('\n')}
            onChange={(e) =>
              set({ expectPaths: e.target.value.split('\n').map((line) => line.trim()).filter(Boolean) })
            }
            rows={3}
            placeholder={'engineering/auth/sessions\none path per line'}
            disabled={disabled}
            className="w-full rounded-md border border-border-strong bg-card px-2 py-1.5 font-mono text-[11px] text-ink"
          />
          <span className="block text-[10px] leading-relaxed text-ink-faint">
            Project-relative paths, as the page's address shows them. The projects below grade which
            projects its citations come from.
          </span>
        </label>
      )}
      <div className="max-h-56 space-y-0.5 overflow-y-auto pr-0.5">
        {projects.map((project) => {
          const expected = expectation.expectProjectIds.includes(project.id);
          const forbidden = expectation.forbidProjectIds.includes(project.id);
          return (
            <div
              key={project.id}
              className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-surface-subtle"
              style={{ paddingLeft: `${6 + project.depth * 12}px` }}
            >
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-[11px]',
                  expected ? 'font-medium text-ink' : forbidden ? 'text-ink-faint line-through' : 'text-ink-muted',
                )}
                title={project.path}
              >
                {project.name}
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggle('expectProjectIds', project.id)}
                title={question ? 'Should cite a page from this project' : 'Should reach this project'}
                className={cn(
                  'flex-shrink-0 rounded p-1 transition-colors disabled:opacity-40',
                  expected ? 'bg-success-bg text-success-text' : 'text-ink-faint hover:text-ink',
                )}
              >
                <Target size={11} />
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={() => toggle('forbidProjectIds', project.id)}
                title={question ? 'Must cite nothing from this project' : 'Must not reach this project'}
                className={cn(
                  'flex-shrink-0 rounded p-1 transition-colors disabled:opacity-40',
                  forbidden ? 'bg-danger-bg text-danger-text' : 'text-ink-faint hover:text-ink',
                )}
              >
                <Ban size={11} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="space-y-2 border-t border-border-subtle pt-2.5">
        <CheckRow
          checked={expectation.exactProjects}
          onChange={(exactProjects) => set({ exactProjects })}
          disabled={disabled}
          label="No projects beyond the expected ones"
          hint={
            question
              ? 'Fails an answer that cites any project not marked above.'
              : 'Off by default — finding a project nobody thought of is what this agent is for, and a grader that failed it would train the wrong thing.'
          }
        />
        {question ? (
          <CheckRow
            checked={expectation.requireAnswered ?? false}
            onChange={(requireAnswered) => set({ requireAnswered })}
            disabled={disabled}
            label="Must answer itself"
            hint="Fails a run where the loop ended without kb_answer and the deterministic fallback wrote the answer."
          />
        ) : (
          <>
            <CheckRow
              checked={expectation.requireConfirmed}
              onChange={(requireConfirmed) => set({ requireConfirmed })}
              disabled={disabled}
              label="Must settle the placement itself"
              hint="Fails a run that hit the step budget before confirming. What it had decided by then is still used, so this catches a placement that is real but unfinished."
            />

            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[10px] font-medium text-ink-secondary">Min claimed %</span>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={expectation.minClaimedPct ?? ''}
                  onChange={(e) => set({ minClaimedPct: e.target.value ? Number(e.target.value) : null })}
                  placeholder="ignore"
                  disabled={disabled}
                  className="text-xs"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[10px] font-medium text-ink-secondary">Max assigned %</span>
                <Input
                  type="number"
                  min={0}
                  max={1000}
                  value={expectation.maxAssignedPct ?? ''}
                  onChange={(e) => set({ maxAssignedPct: e.target.value ? Number(e.target.value) : null })}
                  placeholder="ignore"
                  disabled={disabled}
                  className="text-xs"
                />
              </label>
            </div>
            <p className="text-[10px] leading-relaxed text-ink-faint">
              Set max assigned to about 105 to catch two projects being handed the same sentences — over
              100% is not rounding, it is duplication.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function CheckRow({
  checked,
  onChange,
  disabled,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2">
      <Checkbox
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 flex-shrink-0"
      />
      <span className="min-w-0">
        <span className="block text-[11px] text-ink-secondary">{label}</span>
        <span className="block text-[10px] leading-relaxed text-ink-faint">{hint}</span>
      </span>
    </label>
  );
}
